import { LogIn } from 'lucide-react'
import { Navigate } from 'react-router'

import coverWhaleLogo from '@/assets/marketing/carrier-logos/cover-whale.png'
import geicoLogo from '@/assets/marketing/carrier-logos/geico.png'
import nirvanaLogo from '@/assets/marketing/carrier-logos/nirvana.png'
import progressiveLogo from '@/assets/marketing/carrier-logos/progressive.png'
import relayHeroImg from '@/assets/marketing/images/truck-insurance-hero.jpg'
import { Button } from '@/components/ui/button'
import { appHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { Checklist } from './components/Checklist'
import { NavBar } from './components/NavBar'
import { RelayFaq } from './components/RelayFaq'
import { Reveal } from './components/Reveal'
import { SiteFooter } from './components/SiteFooter'
import { ValueProp } from './components/ValueProp'
import { RELAY_CHECKS, RELAY_FAQS, RELAY_REQUIREMENTS, RELAY_VALUE_PROPS } from './content/amazonRelayContent'

/* Standalone Amazon Relay insurance landing page — own header and footer, no MarketingLayout, same pattern as LargeFleets.tsx. */

const QUOTE_HREF = appHref('/application/start')

const PHONE_NUMBER = '(770) 765-0331'

const EYEBROW = 'font-mono text-xs font-semibold tracking-[0.14em] uppercase'

const HERO_LOGOS = [
  { src: geicoLogo, alt: 'Geico', className: 'h-5.5' },
  { src: progressiveLogo, alt: 'Progressive', className: 'h-8' },
  { src: nirvanaLogo, alt: 'Nirvana', className: 'h-5.5' },
  { src: coverWhaleLogo, alt: 'Cover Whale', className: 'h-5.5' },
]

export function AmazonRelay() {
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="flex min-h-screen w-full shrink-0 flex-col bg-white">
      <title>Cai | Amazon Relay insurance</title>
      <meta
        name="description"
        content="Shop trucking insurance quotes built to meet Amazon Relay's requirements, including Trailer Interchange coverage for power-only carriers."
      />

      <NavBar
        theme="light"
        phoneNumber={PHONE_NUMBER}
        onPhoneClick={() => trackMarketingCta('amazon_relay_nav_phone')}
        utilityLinks={[{ label: 'Login', icon: <LogIn />, href: appHref('/insure') }]}
        ctaLabel="Get your quotes"
        ctaHref={QUOTE_HREF}
        onCtaClick={() => trackMarketingCta('amazon_relay_nav_get_quote', { destination: QUOTE_HREF })}
      />

      {/* Pulled up under the nav by its height (h-14, md:h-16), so the photo, not the page, shows when the nav scrolls away. */}
      <section className="bg-ink-50 border-ink-100 relative -mt-14 flex min-h-[clamp(560px,82vh,860px)] items-end overflow-hidden border-b px-6 pt-[calc(clamp(96px,12vw,160px)+3.5rem)] pb-[clamp(56px,7vw,96px)] md:-mt-16 md:pt-[calc(clamp(96px,12vw,160px)+4rem)]">
        <img
          src={relayHeroImg}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-linear-to-t from-white/95 via-white/80 via-40% to-white/10 to-75%" />
        <Reveal className="relative mx-auto flex w-full max-w-(--maxw) flex-col gap-7">
          <span className={`${EYEBROW} text-brand-800`}>Amazon Relay insurance</span>
          <h1 className="h1-hero max-w-180 text-balance">Shop Relay-ready quotes today</h1>
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              href={QUOTE_HREF}
              onClick={() => trackMarketingCta('amazon_relay_hero_get_quote', { destination: QUOTE_HREF })}
            >
              Get your quotes
            </Button>
            <Button size="lg" variant="outline" href="#requirements" className="bg-transparent">
              See Relay requirements
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-6 pt-3">
            <span className="text-ink-600 font-mono text-xs font-bold tracking-[0.1em] uppercase">
              We shop
            </span>
            {HERO_LOGOS.map((logo) => (
              <img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className={`${logo.className} w-auto opacity-80 grayscale`}
              />
            ))}
            <span className="text-ink-600 font-mono text-xs tracking-[0.06em]">and more</span>
          </div>
        </Reveal>
      </section>

      <section id="requirements" className="scroll-mt-20 bg-white px-6 py-[clamp(64px,8vw,128px)]">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-12">
          <Reveal className="flex max-w-190 flex-col gap-5">
            <span className={`${EYEBROW} text-brand-800`}>Relay requirements</span>
            <h2 className="h2 text-balance">What Amazon needs to see before you haul</h2>
            <p className="text-ink-600 text-lg leading-[1.6] text-pretty">
              Relay checks your coverage against these limits. If anything comes up short, your account gets
              flagged and loads stop. Every Cai quote for Relay is built to clear them.
            </p>
          </Reveal>

          <Reveal delay={100} className="border-ink-200 flex flex-col border-t">
            {RELAY_REQUIREMENTS.map((requirement) => (
              <div
                key={requirement.name}
                className="border-ink-100 grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] items-baseline gap-x-8 gap-y-2 border-b py-5"
              >
                <span className="text-lg font-semibold">{requirement.name}</span>
                <span className="text-md text-brand-800 font-mono leading-[1.6] font-medium">
                  {requirement.limit}
                </span>
                <span className="text-md text-ink-600 leading-[1.6]">{requirement.who}</span>
              </div>
            ))}
          </Reveal>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-6">
            <Reveal className="border-ink-100 bg-ink-50 flex flex-col gap-5 rounded-sm border p-7">
              <h3 className="text-lg font-semibold">Relay also checks</h3>
              <Checklist items={RELAY_CHECKS} />
            </Reveal>
            <Reveal
              delay={100}
              className="border-ink-100 bg-ink-50 flex flex-col gap-4 rounded-sm border p-7"
            >
              <span className={`${EYEBROW} text-brand-800 font-bold`}>Cargo coverage</span>
              <h3 className="text-lg leading-[1.4] font-semibold text-balance">
                $100,000 is the floor, not the goal
              </h3>
              <p className="text-md text-ink-600 leading-[1.6] text-pretty">
                A full trailer of Amazon freight like electronics or appliances can be worth more than that.
                If a load is stolen or damaged, anything over your limit comes out of your pocket. We&apos;ll
                quote higher Cargo coverage limits side by side so you can see what the extra protection
                costs.
              </p>
            </Reveal>
          </div>

          <p className="text-ink-500 font-mono text-xs leading-[1.6]">
            Based on Amazon Relay&apos;s published insurance requirements as of September 2026. Amazon can
            change these at any time.
          </p>
        </div>
      </section>

      <section className="bg-ink-50 px-6 py-[clamp(64px,8vw,128px)]">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-14">
          <Reveal className="flex max-w-190 flex-col gap-5">
            <span className={`${EYEBROW} text-brand-800`}>Why Cai</span>
            <h2 className="h2 text-balance">
              Most insurance brokers sell you a policy. Cai keeps you hauling.
            </h2>
          </Reveal>
          <Reveal
            delay={100}
            className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,250px),1fr))] gap-x-10 gap-y-12"
          >
            {RELAY_VALUE_PROPS.map((prop) => (
              <ValueProp
                key={prop.key}
                icon={prop.icon}
                title={prop.title}
                className="border-ink-200 border-t pt-6"
              >
                {prop.body}
              </ValueProp>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-900 px-6 py-[clamp(64px,8vw,128px)] text-white">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-3 gap-12 max-lg:grid-cols-1">
          <Reveal className="flex flex-col gap-5">
            <span className={`${EYEBROW} text-brand-500`}>FAQ</span>
            <h2 className="h2 text-white">Relay insurance questions</h2>
          </Reveal>
          <Reveal delay={100} className="col-span-2 min-w-0 max-lg:col-span-1">
            <RelayFaq faqs={RELAY_FAQS} />
          </Reveal>
        </div>
      </section>

      <SiteFooter
        hideEdgeLines
        tagline="Financial technology to empower the businesses that build, move, and fix the world around us."
        linkColumns={[
          {
            heading: 'Company',
            links: [
              { label: 'About', href: '/#about' },
              { label: 'Blog', href: 'https://blog.cai.ai', external: true },
            ],
          },
          {
            heading: 'Products',
            links: [
              { label: 'Insurance', href: '/insurance' },
              { label: 'Large fleets', href: '/insurance/large-fleet-coverage' },
              { label: 'FuelApp', href: '/fuelapp' },
            ],
          },
        ]}
        legalLinks={[
          { label: 'Privacy Policy', href: '/privacy' },
          { label: 'Terms of Service', href: '/terms' },
        ]}
        note={
          <>
            <p>
              Cai is not affiliated with or endorsed by Amazon. Amazon Relay is a trademark of Amazon.com,
              Inc. or its affiliates.
            </p>
            <p>
              Insurance services provided by Cai Insurance Services, a service of Cai Labs, Inc. Agency
              License #6018835. Descriptions are general in nature only. Please refer to the terms and
              conditions of any policies offered or purchased. Insurance products offered are subject to
              application and underwriting requirements. Not all products available in all states.
            </p>
          </>
        }
      />
    </div>
  )
}
